import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useNavigate, useLocation } from 'react-router-dom';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import DataTable from 'examples/Tables/DataTable';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Footer from 'examples/Footer';
import MDPagination from 'components/MDPagination';
import { apiGetNoticesTitle } from '../../api/notice.api';
import { useData } from '../../hooks';
import TopNavbar from 'examples/Navbars/TopNavbar';
import { handleDownloadExcel } from '../../components/excel/excelUtils';

const Boards = () => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, appIsReady } = useData();

  // URL에서 상태를 복원합니다.
  const searchParams = new URLSearchParams(location.search);
  const initialMessageType = parseInt(searchParams.get('messageType')) || 1;
  const [messageType, setMessageType] = useState(initialMessageType);

  useEffect(() => {
    if (appIsReady && user) {
      fetchNotices(page, messageType);
    }
  }, [page, messageType, user, appIsReady]);

  const fetchNotices = async (pageNum, messageType) => {
    setLoading(true);
    try {
      const response = await apiGetNoticesTitle(pageNum, 10, messageType, user.accessToken);
      if (response && response.data) {
        setNotices(response.data);
        setTotal(response.total);
      } else {
        console.log('No data received');
      }
    } catch (error) {
      console.error('Failed to load notices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNum) => {
    setPage(pageNum);
  };

  const handleRowClick = (noticeData) => {
    navigate(`/board/detail/${noticeData.id}`, { state: { messageType, page } });
  };

  const handleMessageTypeChange = (event, newValue) => {
    setMessageType(newValue);
    setPage(1); // 새로운 메시지 타입으로 검색 시 페이지 번호를 1로 초기화

    // URL을 업데이트합니다.
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('messageType', newValue);
    navigate({ search: newSearchParams.toString() });
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
    return new Date(dateString).toLocaleString('ko-KR', options).replace(/. /g, '-').replace('.', '').replace(' ', ' ').slice(0, 10);
  };

  const messageTypeToString = (type) => {
    switch (type) {
      case 1: return '앱공지';
      case 3: return '매뉴얼';
      case 4: return '점주용공지';
      case 5: return '점주용매뉴얼';
      case 6: return '후원사';
      default: return '기타';
    }
  };

  const rows = notices.map(notice => ({
    original: notice,
    id: notice.id ? notice.id.toString() : "",  // 숫자를 문자열로 변환
    title: notice.title || "",  // null 체크
    creator_id: notice.creator_id || "",  // null 체크
    messageType: notice.messageType !== null && notice.messageType !== undefined ? messageTypeToString(notice.messageType) : "알 수 없음",  // messageType을 문자열로 변환
    subType: notice.subType !== null && notice.subType !== undefined ? notice.subType.toString() : "",  // 숫자를 문자열로 변환
    orderno: notice.orderno !== null && notice.orderno !== undefined ? notice.orderno.toString() : "",  // 숫자를 문자열로 변환
    createdAt: notice.createdAt ? formatDate(notice.createdAt) : "",  // null 체크
  }));

  const columns = [
    { Header: "ID", accessor: "id", align: "left" },
    { Header: "제목", accessor: "title", align: "left" },
    { Header: "작성자 ID", accessor: "creator_id", align: "left" },
    { Header: "메시지 타입", accessor: "messageType", align: "center" },
    ...(messageType === 3 || messageType === 5 ? [
      { Header: "서브 타입", accessor: "subType", align: "center" },
      { Header: "순서", accessor: "orderno", align: "center" }
    ] : []),
    { Header: "작성일", accessor: "createdAt", align: "left" },
  ];

  // 페이지 번호 계산
  const totalPages = Math.ceil(total / 10);


  return (
    <DashboardLayout>
      <TopNavbar />
      <MDBox display="flex" mr={2} justifyContent="right">
        <MDButton variant="gradient" color="success" onClick={() => navigate(`/board/insert`)}>게시물등록</MDButton>
        <MDButton variant="gradient" color="info" onClick={() => handleDownloadExcel('게시물', columns, rows)} sx={{ ml: 2 }}>엑셀 다운로드</MDButton>
      </MDBox>
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  게시물관리
                </MDTypography>
              </MDBox>
              <MDBox pt={3} px={2}>
                <Tabs
                  value={messageType}
                  onChange={handleMessageTypeChange}
                  indicatorColor="primary"
                  textColor="primary"
                  centered
                >
                  <Tab label="앱공지" value={1} />
                  <Tab label="매뉴얼" value={3} />
                  <Tab label="점주용공지" value={4} />
                  <Tab label="점주용매뉴얼" value={5} />
                  <Tab label="후원사" value={6} />
                </Tabs>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                  loading={loading}
                  onRowClick={(row) => handleRowClick(row.original)}
                />
              </MDBox>
              <MDBox
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                justifyContent="center"
                alignItems={{ xs: "flex-start", sm: "center" }}
                p={3}
              >
                <MDPagination
                  variant="gradient"
                  color="info"
                >
                  {page > 1 && (
                    <MDPagination item onClick={() => handlePageChange(page - 1)}>
                      <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                    </MDPagination>
                  )}
                  {Array.from({ length: totalPages }, (_, index) => (
                    <MDPagination
                      item
                      key={index}
                      onClick={() => handlePageChange(index + 1)}
                      active={page === index + 1}
                    >
                      {index + 1}
                    </MDPagination>
                  ))}
                  {page < totalPages && (
                    <MDPagination item onClick={() => handlePageChange(page + 1)}>
                      <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                    </MDPagination>
                  )}
                </MDPagination>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Boards;
